/**
 * PayPal 整页跳转回跳 URL 参数解析与清洗。
 *
 * Safari 无痕可能剥光跨站回跳 query；下单时 return_url 会写入 sessionStorage，
 * 落地裸 /reader|/pay 后由 paypal-return-restore 同源 replace 恢复（见 PayPalReturnConfirmHandler）。
 *
 * 后端构造的实际回跳 URL 示例：
 *   订阅成功:
 *     /reader?...&option=success&order_id=<commerceOrderId>&subscription_id=I-XXXX
 *            &ba_token=BA-XXX&token=XXX&return_scene=reader&trigger_drama_id=...
 *   一次性充值成功:
 *     /pay?...&option=approve&order_id=<commerceOrderId>&token=EC-XXX&PayerID=...
 *   取消:
 *     /reader?...&option=cancelled&order_id=<commerceOrderId>&token=XXX&return_scene=reader&...
 *     注：PayPal 实际回传 option=cancelled（双 l），后端也可能使用 option=cancel（单 l），两者均兼容。
 *
 * 字段说明：
 * - `option`           回跳结果：'success'（订阅）、'approve'（一次性）、'cancel'/'cancelled'（取消）
 * - `order_id`         commerce order_id
 * - `subscription_id`  PayPal 订阅 store order id（I-XXXX）
 * - `token`            一次性 EC token，作 store_order_id 回填（confirm status=approve）
 * - `ba_token`         PayPal billing agreement token（透传/排错）
 * - `return_scene` / `trigger_drama_id`：发起下单时由前端传入，被后端回声到 URL；处理完成后应一并清掉，
 *   避免 F5 时把回声当成"新的回跳"误解析。
 */
export enum EPayPalReturnQueryKey {
    OrderId = 'order_id',
    SubscriptionId = 'subscription_id',
    Option = 'option',
    BaToken = 'ba_token',
    Token = 'token',
    ReturnScene = 'return_scene',
    TriggerDramaId = 'trigger_drama_id',
}

export type TPayPalReturnStatus = 'success' | 'cancel' | 'approve';

export interface IPayPalReturn {
    /** commerce order_id；cancel 时也会被后端带回 */
    orderId: string;
    /** PayPal store order id：订阅用 subscription_id，一次性 redirect 可用 token */
    storeOrderId: string;
    /** PayPal billing agreement token */
    baToken: string;
    /** PayPal EC token */
    token: string;
    status: TPayPalReturnStatus;
}

/** next/navigation ReadonlyURLSearchParams 与 URLSearchParams 的共有最小接口 */
interface IReadonlySearchParams {
    get: (name: string) => string | null;
}

function pickStoreOrderId(params: IReadonlySearchParams): string {
    const fromSubscription = params.get(EPayPalReturnQueryKey.SubscriptionId)?.trim();
    if (fromSubscription) {
        return fromSubscription;
    }
    return params.get(EPayPalReturnQueryKey.Token)?.trim() ?? '';
}

/**
 * 从 URL 查询参数解析 PayPal 回跳信息。
 * - `option` 不是 success / approve / cancel / cancelled → 返回 null（与本次跳转无关）
 * - success / approve：至少需要 `order_id`；`storeOrderId` 优先 subscription_id，否则 token
 * - `option === 'cancel' | 'cancelled'` → 视为合法（即使其他字段缺失也允许走 cancel 处理）
 */
export function parsePayPalReturnFromSearchParams(params: IReadonlySearchParams): IPayPalReturn | null {
    const rawStatus = params.get(EPayPalReturnQueryKey.Option);
    const isCancel = rawStatus === 'cancel' || rawStatus === 'cancelled';
    const isSuccess = rawStatus === 'success' || rawStatus === 'approve';
    if (!isCancel && !isSuccess) {
        return null;
    }

    const orderId = params.get(EPayPalReturnQueryKey.OrderId) ?? '';
    const storeOrderId = pickStoreOrderId(params);
    const baToken = params.get(EPayPalReturnQueryKey.BaToken) ?? '';
    const token = params.get(EPayPalReturnQueryKey.Token) ?? '';

    if (isSuccess && !orderId) {
        return null;
    }

    if (isCancel) {
        return { orderId, storeOrderId, baToken, token, status: 'cancel' };
    }

    return {
        orderId,
        storeOrderId,
        baToken,
        token,
        status: rawStatus === 'approve' ? 'approve' : 'success',
    };
}

/** 处理完回跳之后需要从 URL 上抹掉的字段（PayPal 回调 + 我们发起时的回声字段） */
const STRIPPED_QUERY_KEYS: readonly string[] = [
    EPayPalReturnQueryKey.OrderId,
    EPayPalReturnQueryKey.SubscriptionId,
    EPayPalReturnQueryKey.Option,
    EPayPalReturnQueryKey.BaToken,
    EPayPalReturnQueryKey.Token,
    EPayPalReturnQueryKey.ReturnScene,
    EPayPalReturnQueryKey.TriggerDramaId,
    'PayerID',
];

/**
 * 返回去掉 PayPal 回跳相关 query 之后的 pathname + search + hash，
 * 供 router.replace() 调用以避免 F5 重提。
 * 保留业务页自身需要消费的参数（如 reader 的 `drama_id` / `chapter_id`）。
 */
export function stripPayPalReturnFromUrl(url: string): string {
    try {
        const u = new URL(url);
        STRIPPED_QUERY_KEYS.forEach(key => u.searchParams.delete(key));
        const search = u.searchParams.toString();
        return `${u.pathname}${search ? `?${search}` : ''}${u.hash ?? ''}`;
    } catch {
        return url;
    }
}
